import React, { ChangeEvent } from 'react';
import * as fuzzy from 'fuzzy';
import { castArray } from 'lodash';
import { Checkbox } from 'antd';
import { Flex } from '@ui-library/misc';
import { LabelLarge } from '@ui-library/typography';
import { SearchOutlined } from '@ant-design/icons';
import Button from '@ui-library/button';
import { SelectedCount } from './styles';
import TreeBranch from './tree';
import Input from '@ui-library/input';

export interface Resource {
  default?: boolean;
  name: string;
  id?: string;
  resource: string;
  children?: Resource[];
  columns?: boolean;
}

interface NormalizedResource extends Resource {
  selectedChildrenNumber?: number;
  selectionStatus?: 'all' | 'none' | 'some' | 'self';
  selected?: boolean;
  hasChildren?: boolean;
  shouldShow?: boolean;
  custom?: boolean;
  children?: NormalizedResource[];
  parents?: string[];
}

export interface SelectData {
  resource: NormalizedResource;
  value: string;
  checked: boolean;
  name?: string;
  id: string;
}

export interface ResourceSelectionProps {
  className?: string;
  availableResources: Resource[];
  selectedResources?: string[];
  onChange?: (selectedData: string[], hasChange: boolean) => void;
  expandable?: boolean;
  expanded?: boolean;
  columns?: number;
  disabled?: boolean;
  onSingleResourceOrLess?: () => void;
  style?: React.CSSProperties;
  selectRef?: (selectElement: {
    toggleSelect: (id: string, forceSelectState?: boolean) => void;
  }) => void;
}

interface INormalizedResourceSelection {
  resources: NormalizedResource[];
  withChildren: string[];
  selectedValues: string[];
  resourceStatuses: Record<string, string>;
}

interface State {
  selectAll?: boolean;
  searchTerm?: string;
  selectedResources?: string[];
  normalizedResourceSelection?: INormalizedResourceSelection;
  lastClicked?: { resource: string; parents: string[] };
}

const getAllResources = (normalizedResourceSelection: INormalizedResourceSelection) =>
  normalizedResourceSelection?.resources.reduce((accum: string[], resource: NormalizedResource) => {
    const resourceToAdd = ResourceSelection.getDeepResources(resource);
    accum = [...resourceToAdd, resource.name || resource.resource, ...accum];
    return accum;
  }, []);

export class ResourceSelection extends React.PureComponent<ResourceSelectionProps, State> {
  constructor(props: ResourceSelectionProps) {
    super(props);
    this.state = {
      selectAll: false,
      lastClicked: undefined,
      selectedResources: this.props.selectedResources,
      normalizedResourceSelection: undefined,
      searchTerm: undefined,
    };
  }

  // Compares 2 arrays and return true if are equals else false
  static arrayCompare = (array1: string[] = [], array2: string[] = []) =>
    array1 &&
    array2 &&
    array1.length === array2.length &&
    array1.every((value: string) => array2.includes(value));

  static getDerivedStateFromProps(props: ResourceSelectionProps, state: State) {
    const { selectedResources, lastClicked, searchTerm, normalizedResourceSelection } = state;
    let resourcesSelected = selectedResources;
    if (!selectedResources) {
      resourcesSelected = ResourceSelection.getDefaults(props.availableResources);
    }
    const resourceSelection = ResourceSelection.transformResourceSelection(
      props.availableResources,
      resourcesSelected,
      normalizedResourceSelection && normalizedResourceSelection.resourceStatuses,
      lastClicked,
      searchTerm,
    );

    return {
      selectedResources: resourceSelection.selectedValues,
      normalizedResourceSelection: resourceSelection,
    };
  }

  componentDidMount() {
    const { onChange, selectRef } = this.props;
    if (selectRef) {
      selectRef({
        toggleSelect: this.toggleSelect,
      });
    }
    if (onChange) {
      onChange(this.state.selectedResources as string[], false);
    }
  }

  static getDefaults = (availableResources: Resource[]) =>
    availableResources.reduce(function itter(defaults: string[], resource) {
      if (resource.default) {
        defaults.push(resource.id || resource.resource);
      }
      if (resource.children) {
        resource.children.reduce(itter, defaults);
      }
      return defaults;
    }, []);

  static transformResourceSelection = (
    resources: Resource[],
    selectedResources?: string[],
    prevResourceStates?: Record<string, string>,
    lastClicked?: State['lastClicked'],
    searchTerm?: string,
  ) => {
    const resourcesWithChildren: string[] = [];
    const selectedValues: Set<string> = new Set();
    const resourceStatuses: Record<string, string> = {};
    const normalizer = (
      resources: Resource[],
      selectedResources: string[],
      parents: string[] = [],
    ) => {
      let selectedFieldsCount = 0;
      const selectionStatuses: NormalizedResource['selectionStatus'][] = [];
      const visibilityStatuses: boolean[] = [];
      const normalizedContent: NormalizedResource[] = resources.map(
        (resource: Resource): NormalizedResource => {
          let selectedChildren = 0;
          let newChildren: Resource[];
          let selectedChildrenNumber = 0;
          let selected = false;
          let hasChildren = false;
          let selectionStatus: NormalizedResource['selectionStatus'];
          let visibilityStatus: boolean;
          const isSelected =
            selectedResources && selectedResources.includes(resource.id || resource.resource);
          if (resource.children && resource.children.length > 0) {
            hasChildren = true;
            const normalizedChildren = normalizer(resource.children, selectedResources, [
              ...parents,
              resource.resource,
            ]);
            newChildren = normalizedChildren.normalizedContent;
            selectedChildren = normalizedChildren.selectedFieldsCount;
            selectedChildrenNumber = selectedChildren;
            resourcesWithChildren.push(resource.resource);
            const hasSome = normalizedChildren.selectionStatuses.includes('some');
            const hasAll = normalizedChildren.selectionStatuses.includes('all');
            const hasNone = normalizedChildren.selectionStatuses.includes('none');
            const hasSelf = normalizedChildren.selectionStatuses.includes('self');

            if (hasSome || ((hasAll || hasSelf) && hasNone)) {
              selectionStatus = 'some';
            } else if ((hasAll || hasSelf) && !hasNone) {
              selectionStatus = 'all';
            } else if (
              isSelected ||
              (isSelected && lastClicked && lastClicked.resource === resource.resource)
            ) {
              selectionStatus = 'self';
            } else {
              selectionStatus = 'none';
            }
            if (
              prevResourceStates &&
              lastClicked &&
              !parents.includes(lastClicked.resource) &&
              lastClicked.resource !== resource.resource &&
              !lastClicked.parents.includes(resource.resource)
            ) {
              selectionStatus = prevResourceStates[
                resource.resource
              ] as NormalizedResource['selectionStatus'];
            }
            visibilityStatus = searchTerm
              ? normalizedChildren.visibilityStatuses.includes(true) ||
                !!fuzzy.match(searchTerm, resource.name || resource.resource)
              : true;
          }
          if (isSelected || selectedChildren) {
            selectedFieldsCount += 1;
            selected = true;
          }
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (typeof visibilityStatus !== 'boolean') {
            visibilityStatus = searchTerm
              ? !!fuzzy.match(searchTerm, resource.name || resource.resource)
              : true;
          }
          if (!selectionStatus) {
            selectionStatus = selected ? 'self' : 'none';
          }
          resourceStatuses[resource!.id || resource!.resource] = selectionStatus;
          if (
            ['all', 'some'].includes(selectionStatus) ||
            (selectionStatus === 'self' && isSelected)
          ) {
            selectedValues.add(resource.id || resource!.resource);
          }
          selectionStatuses.push(selectionStatus);
          visibilityStatuses.push(visibilityStatus);
          const result: NormalizedResource = {
            ...resource,
            selectionStatus,
            selected,
            parents,
            hasChildren,
            shouldShow: visibilityStatus,
          };
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (newChildren) {
            result.selectedChildrenNumber = selectedChildrenNumber;
            result.children = newChildren;
          }
          return result;
        },
      );
      return {
        normalizedContent,
        selectedFieldsCount,
        selectionStatuses,
        visibilityStatuses,
      };
    };
    const result = normalizer(resources, selectedResources as string[]);

    return {
      resourceStatuses,
      resources: result.normalizedContent,
      withChildren: resourcesWithChildren,
      selectedValues: Array.from(selectedValues),
    };
  };

  static getDeepResources = (resource: NormalizedResource) => {
    if (resource.children) {
      return resource.children.reduce(function getDeep(newArray: string[], val) {
        if (val.children) {
          newArray.push(val.resource);
          val.children.reduce(getDeep, newArray);
        } else {
          newArray.push(val.resource);
        }
        return newArray;
      }, []);
    }
    return [resource.id || resource.resource];
  };

  unCheck = (data: SelectData, value?: string | string[]) => {
    const { selectedResources } = this.state;

    let resourcesToRemove = castArray(value);
    if (!value) {
      resourcesToRemove = ResourceSelection.getDeepResources(data.resource);
      resourcesToRemove = [...resourcesToRemove, data.value];
    }
    const newResources = (selectedResources as string[]).filter(
      (value) => !resourcesToRemove.includes(value),
    );
    return newResources;
  };

  check = (
    data: SelectData,
    value?: string | string[],
    selectedResources: string[] = this.state.selectedResources as string[],
  ) => {
    let resourceToAdd = castArray(value);
    if (!value) {
      resourceToAdd = ResourceSelection.getDeepResources(data.resource);
      resourceToAdd = [...resourceToAdd, data.value];
    }
    const resourceSet = new Set([...selectedResources, ...resourceToAdd, data.value]);
    return Array.from(resourceSet);
  };

  setSelectedResources = (data: SelectData, cl?: (newSelection: string[]) => void) => {
    const { selectedResources } = this.state;
    let newResources;
    const isParent = !!data.resource.children && data.resource.children.length > 0;
    if (!data.checked && isParent && data.resource.selectionStatus === 'none') {
      newResources = this.check(data);
    } else if (data.checked && isParent && data.resource.selectionStatus === 'all') {
      newResources = this.unCheck(data);
      newResources = this.check(data, data.value, newResources);
    } else if (data.checked) {
      newResources = this.unCheck(data, data.value);
    } else {
      newResources = this.check(data);
    }
    if (newResources) {
      this.setState(
        {
          selectedResources: newResources,
          lastClicked: { resource: data.value, parents: data.resource.parents! },
        },
        () => {
          if (!cl) {
            return;
          }
          const { selectedResources } = this.state;
          cl(selectedResources as string[]);
        },
      );
    }
    return newResources || selectedResources;
  };

  /* Change the state of all resources to the opposite state.
   */
  toggleAll = () => {
    if (this.props.disabled) {
      return;
    }
    const { onChange, selectedResources } = this.props;
    const { normalizedResourceSelection, selectAll } = this.state;
    const newState: State = {};
    if (!selectAll) {
      newState.selectedResources = [];
      newState.lastClicked = undefined;
    } else {
      const allResources = normalizedResourceSelection
        ? getAllResources(normalizedResourceSelection)
        : undefined;
      newState.selectedResources = Array.from(new Set(allResources));
    }
    newState.selectAll = !selectAll;
    this.setState(newState);
    if (onChange) {
      onChange(
        newState.selectedResources,
        !ResourceSelection.arrayCompare(selectedResources as string[], newState.selectedResources),
      );
    }
  };

  toggleSelect = (resourceId: string, forceSelectState?: boolean) => {
    const { onChange } = this.props;
    const { selectedResources } = this.state;
    const newState: State = {};
    if (
      forceSelectState === false ||
      ((selectedResources as string[]).includes(resourceId) && forceSelectState !== true)
    ) {
      newState.selectedResources = (selectedResources as string[]).filter(
        (resource) => resource !== resourceId,
      );
    } else {
      newState.selectedResources = [...(selectedResources as string[]), resourceId];
    }

    this.setState(newState);
    if (onChange) {
      onChange(
        newState.selectedResources,
        !ResourceSelection.arrayCompare(
          this.props.selectedResources as string[],
          newState.selectedResources,
        ),
      );
    }
  };

  onSelect = (_: unknown, data: SelectData) => {
    const { onChange, disabled, selectedResources = [] } = this.props;
    if (disabled) {
      return;
    }
    this.setSelectedResources(data, (newSelection) => {
      if (onChange) {
        onChange(newSelection, !ResourceSelection.arrayCompare(selectedResources, newSelection));
      }
    });
  };

  componentDidUpdate(prevProps: ResourceSelectionProps) {
    const { availableResources, selectedResources, onChange, onSingleResourceOrLess } = this.props;
    const { normalizedResourceSelection } = this.state;
    const resources = availableResources.map((resource) => resource.id || resource.resource);
    const prevResources = prevProps.availableResources.map(
      (resource) => resource.id || resource.resource,
    );
    /* If available resources change or default selectedResource,
    remove any old resource from selectedResources */
    if (
      !ResourceSelection.arrayCompare(resources, prevResources) ||
      !ResourceSelection.arrayCompare(prevProps.selectedResources, selectedResources)
    ) {
      const allResources = normalizedResourceSelection
        ? getAllResources(normalizedResourceSelection)
        : [];
      const filteredResources = (this.state.selectedResources as string[]).filter((resource) =>
        allResources.includes(resource),
      );
      if (onChange) {
        onChange(
          filteredResources,
          !ResourceSelection.arrayCompare(selectedResources as string[], filteredResources),
        );
      }
    }
    if (
      !this.singleResourceCallbackCalled &&
      onSingleResourceOrLess &&
      availableResources &&
      availableResources.length < 2
    ) {
      this.singleResourceCallbackCalled = true;
      onSingleResourceOrLess();
    }
  }

  filterSelection = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      searchTerm: e.target.value,
    });
  };

  singleResourceCallbackCalled = false;

  render() {
    const { expanded = true, expandable, disabled } = this.props;
    const { normalizedResourceSelection, selectedResources, searchTerm, selectAll } = this.state;
    const normalResources = normalizedResourceSelection?.resources
      .filter((r) => !r.custom)
      .sort((b: Resource, a: Resource) => {
        const aTxt = a.resource.toLocaleLowerCase();
        const bTxt = b.resource.toLocaleLowerCase();
        if (aTxt < bTxt) {
          return -1;
        }
        if (aTxt > bTxt) {
          return 1;
        }
        return 0;
      });
    const customResources = normalizedResourceSelection?.resources.filter(
      (r) => !!r.custom && !!r.shouldShow,
    );

    return (
      <div style={{ marginBottom: 0, minWidth: 500 }} className="resource-selection__container">
        <Flex
          alignItems="center"
          style={{ marginBottom: 24 }}
          className="resource-selection__header"
        >
          <LabelLarge style={{ flex: 1, marginRight: 12 }}>Select data to import</LabelLarge>
          <div className="resource-selection__header-search-container">
            <Input
              placeholder="SEARCH"
              onChange={this.filterSelection}
              prefix={<SearchOutlined />}
            />
            <Flex>
              <SelectedCount className="resource-selection__header-selected-count">{`${
                selectedResources?.length
              }/${
                Object.keys(normalizedResourceSelection!.resourceStatuses).length
              } selected`}</SelectedCount>
              {!disabled && !searchTerm ? (
                <Button
                  secondary
                  style={{ display: 'none', marginLeft: '16px' }}
                  onClick={this.toggleAll}
                  name={selectAll ? 'Select all' : 'Unselect all'}
                />
              ) : null}
            </Flex>
          </div>
        </Flex>
        <div className="resource-selection__select-all">
          {(!searchTerm && (
            <Checkbox
              style={{ fontWeight: 700, marginBottom: 8, fontSize: 12 }}
              onChange={() => this.toggleAll()}
              checked={!selectAll}
              indeterminate={
                !!(
                  selectedResources?.length &&
                  selectedResources?.length < normalizedResourceSelection!.resources?.length
                )
              }
            >
              SELECT ALL
            </Checkbox>
          )) ||
            null}
        </div>

        <div
          style={{
            maxHeight: 400,
            marginTop: 0,
            paddingTop: 4,
            overflowY: 'auto',
            width: '100%',
          }}
          className="resource-selection__tree-branch-container"
        >
          <TreeBranch
            searchTerm={searchTerm}
            initial
            expandable={expandable}
            expanded={expanded && normalizedResourceSelection?.withChildren}
            onSelect={this.onSelect}
            resources={normalResources}
          />
        </div>
        {customResources && customResources.length > 0 ? (
          <LabelLarge className="resource-selection__custom-resources-label">
            Custom Resources
          </LabelLarge>
        ) : null}
        {(customResources && customResources.length > 0 && (
          <div
            style={{ marginTop: 12, marginBottom: 4 }}
            className="resource-selection__tree-branch-container"
          >
            <TreeBranch
              searchTerm={searchTerm}
              initial
              expandable={expandable}
              expanded={expanded && normalizedResourceSelection?.withChildren}
              onSelect={this.onSelect}
              resources={customResources}
            />
          </div>
        )) ||
          null}
      </div>
    );
  }
}
